// Canonical card-filter primitive. The single function every consumer
// uses to decide "does this card match these criteria?" Scope
// resolution, condition gates, ledger cost modifiers, replacement
// watchers. Replaces four parallel `matchesFilter` impls that each
// knew a different subset of fields.
//
// Filter is a plain JSON object; missing fields = "don't care." A
// completely empty filter matches every card.
//
// ── Card-shape fields ────────────────────────────────────────────────
//   type            'Minion' | 'Avatar' | 'Site' | 'Magic' | 'Artifact' | 'Unit'
//                   Virtual 'Unit' matches Minion OR Avatar (Sorcery's
//                   rulebook noun for "any minion or avatar").
//   subtype         string | string[]. Case-insensitive; any-of when array
//   subtypes        string[]. Legacy alias for subtype (scope.js callers)
//   name            exact card-name match
//   name_words      string | string[]. Whole-word match in card.name
//                   (Templar's "Knight" must NOT match "Knighthood";
//                   "Sirian" must NOT match "Sir")
//   element         single lowercase element key
//                   ('air' | 'earth' | 'fire' | 'water')
//
// ── Controller / relation (relative to ctx.source) ───────────────────
//   controller      'self' | 'enemy'
//   allied          true → same controller as ctx.source
//   enemy           true → opposite controller from ctx.source
//   exclude_self    true → skip the source card itself
//
// ── Status / state ───────────────────────────────────────────────────
//   status              status keyword the card must have (effective:
//                       includes aura grants and respects disabled cards)
//   summoned_this_turn  true → card has summoning sickness
//   currently_tapped    true / false → tap state
//
// ── Region ───────────────────────────────────────────────────────────
//   region          'surface' | 'underground' | 'underwater' | 'void' |
//                   'subsurface' (string | string[], any-of).
//                   'subsurface' matches any below-the-surface region.
//                   When ctx supplies board cell context (meshes +
//                   cellAt + cardMesh) the TRUE region is computed,
//                   including 'void' (a minion on a site-less cell.
//                   Stargazer's "minion in the void"). Without cell
//                   context it falls back to card.subsurface only
//                   (surface / underground / underwater). Lets effects
//                   encode "atop a site" (Thunderstorm) vs "below".
//
// ── Position ─────────────────────────────────────────────────────────
//   cells           [{col, row}]. Card's current cell must be one of
//                   these. Two evaluation paths:
//                     1. ctx.destinationCell present (ledger cost-mods
//                        passing the cast's drop cell)
//                     2. ctx.cellAt + ctx.cardMesh (board-scan resolution)
//                   If neither is available, the field passes (preserves
//                   pre-existing ledger behaviour).
//
// ── Composites (recursive) ───────────────────────────────────────────
//   any_of          CardFilter[]. At least one sub-filter must match (OR)
//   or_filters      CardFilter[]. Legacy alias for any_of
//   all_of          CardFilter[]. Every sub-filter must match (AND)
//   not             CardFilter. Sub-filter must NOT match (negation)
//
// ── Context ──────────────────────────────────────────────────────────
// All ctx fields are optional. When a filter references a field the
// caller can't resolve (e.g. `status` without `hasEffectiveStatus`),
// the filter falls back to a sensible default rather than throwing.
//
//   source                cardInstance. For self-exclude + controller compare
//   effectiveRotated      boolean. Override for source.rotated
//   hasEffectiveStatus    fn(card, keyword) → bool. Aura-aware status check
//   destinationCell       {col,row}. For `cells` field in cost-mod context
//   cellAt                fn(x, z) → {col,row}. For `cells` field in scan context
//   cardMesh              mesh. Paired with cellAt to resolve the card's cell

import { countsAsMinion, countsAsUnit } from '../core/typeline';
import { effectiveSiteThresholds } from '../core/regions';
import { hasEffectiveStatus as defaultHasEffectiveStatus } from './scope';
import { expandSubtypeAliases } from './subtypeAliases';
import { currentRegion } from './regionEntry';
import { currentRegion as currentRegionAtCell } from '../core/movement';
import { siteAtCell } from './ops/siteOccupancy';

/**
 * The card's rules-level subtype tokens (lowercase). Applies the
 * per-pass `_auraSubtypeMap` written by TRANSFORM_SUBTYPES auras
 * (Corruptor: beast → monster), so every consumer. Filters, watch
 * conditions, cost mods. Sees the transformed identity. Exported as
 * the single canonical subtype read.
 */
export function effectiveSubtypes(card) {
  const raw = card.subTypes ?? card.traits;
  const printed = (Array.isArray(raw) ? raw : [raw])
    .filter(Boolean)
    .flatMap((s) => String(s).toLowerCase().split(/[,;\s]+/))
    .filter(Boolean);
  const map = card._auraSubtypeMap;
  if (!map) return printed;
  return printed.map((s) => map[s] || s);
}

/**
 * Persecutor's brand: `branded` (manual status or aura-granted) makes
 * the card count as Evil for subtype filters until end of turn.
 */
export function isBrandedEvil(card) {
  return (card.statuses || []).includes('branded')
    || (card._auraStatuses || []).includes('branded');
}

/**
 * Whole-word name match with a plural fold. The filter token is the
 * printed singular ("Knight", "Sir", "Dame") but card names may
 * pluralize ("Vanguard Knights"), so a name word also matches as
 * `token + 's'`. Still whole-word: "Knighthood" and "Sirian" don't
 * match. Shared with conditions.js's watch_target_filter.
 *
 * @param {string} name - the card name to test
 * @param {string|string[]} wanted - filter token(s), any case
 * @returns {boolean}
 */
export function nameWordsMatch(name, wanted) {
  const tokens = (Array.isArray(wanted) ? wanted : [wanted])
    .map((s) => String(s).toLowerCase());
  const words = String(name || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  return tokens.some((t) => words.includes(t) || words.includes(`${t}s`));
}

function resolveCardCell(card, ctx) {
  if (ctx.cardCell) return ctx.cardCell;
  if (ctx.cellAt && ctx.cardMesh) {
    return ctx.cellAt(ctx.cardMesh.position.x, ctx.cardMesh.position.z);
  }
  return null;
}

export function matchesFilter(card, filter, ctx = {}) {
  if (!filter || Object.keys(filter).length === 0) return true;
  if (!card) return false;

  // ── Composites (evaluated first so they can be combined with other
  //    fields at the same level) ────────────────────────────────────
  const orList = filter.any_of || filter.or_filters;
  if (Array.isArray(orList) && orList.length > 0) {
    if (!orList.some((f) => matchesFilter(card, f, ctx))) return false;
  }
  if (Array.isArray(filter.all_of) && filter.all_of.length > 0) {
    if (!filter.all_of.every((f) => matchesFilter(card, f, ctx))) return false;
  }
  if (filter.not) {
    if (matchesFilter(card, filter.not, ctx)) return false;
  }

  // ── Self-exclude ────────────────────────────────────────────────
  if (filter.exclude_self === true && ctx.source?.id && card.id === ctx.source.id) {
    return false;
  }

  // ── Controller ──────────────────────────────────────────────────
  const sourceRotated = ctx.effectiveRotated ?? ctx.source?.rotated;
  if (sourceRotated !== undefined) {
    if (filter.controller === 'self' && !!card.rotated !== !!sourceRotated) return false;
    if (filter.controller === 'enemy' && !!card.rotated === !!sourceRotated) return false;
    if (filter.allied === true && !!card.rotated !== !!sourceRotated) return false;
    if (filter.enemy === true && !!card.rotated === !!sourceRotated) return false;
  }

  // ── Type (with 'Unit' virtual) ──────────────────────────────────
  // Automatons are artifacts that are minions (codex), so a 'Minion'
  // or 'Unit' filter matches them while their printed 'Artifact'
  // type keeps matching artifact filters too.
  if (filter.type) {
    if (filter.type === 'Unit') {
      if (!countsAsUnit(card)) return false;
    } else if (filter.type === 'Minion') {
      if (!countsAsMinion(card)) return false;
    } else if (card.type !== filter.type) {
      return false;
    }
  }

  // ── Subtype / subtypes (any-of, case-insensitive) ───────────────
  const wantedSubtypes = [];
  if (filter.subtype) {
    const ws = Array.isArray(filter.subtype) ? filter.subtype : [filter.subtype];
    for (const w of ws) wantedSubtypes.push(String(w).toLowerCase());
  }
  if (Array.isArray(filter.subtypes)) {
    for (const w of filter.subtypes) wantedSubtypes.push(String(w).toLowerCase());
  }
  if (wantedSubtypes.length > 0) {
    const subs = effectiveSubtypes(card);
    // Expand synthetic alignment aliases ('evil' → demon/monster/undead)
    // so card text like "Evil minions" matches every concrete Evil
    // subtype without each filter needing to spell out the superset.
    const expanded = expandSubtypeAliases(wantedSubtypes);
    // Persecutor's brand: a card carrying the `branded` status is
    // treated as Evil for the rest of the turn, whatever its printed
    // subtypes say.
    const brandedEvil = wantedSubtypes.includes('evil') && isBrandedEvil(card);
    if (!brandedEvil && !expanded.some((w) => subs.includes(w))) return false;
  }

  // ── Name (exact, then whole-word) ───────────────────────────────
  // Exact match consults aura-granted aliases too: ADD_NAME_ALIAS
  // ("Counts as a Grey Wolves") pushes the alias onto
  // `_auraNameAliases`, rebuilt every recomputeStaticAuras pass.
  if (filter.name
      && card.name !== filter.name
      && !(card._auraNameAliases || []).includes(filter.name)) return false;
  if (filter.name_words) {
    if (!nameWordsMatch(card.name, filter.name_words)) return false;
  }

  // ── Rarity (looked up via catalog when not on the live instance) ─
  if (filter.rarity) {
    const wanted = (Array.isArray(filter.rarity) ? filter.rarity : [filter.rarity])
      .map((s) => String(s).toLowerCase());
    let cardRarity = card.rarity;
    if (!cardRarity && ctx.getFullCard && card.cardId) {
      cardRarity = ctx.getFullCard(card.cardId)?.rarity;
    }
    cardRarity = String(cardRarity || '').toLowerCase();
    if (!wanted.includes(cardRarity)) return false;
  }

  // ── Element ─────────────────────────────────────────────────────
  if (filter.element) {
    const wanted = String(filter.element).toLowerCase();
    // Three shapes in the wild: raw catalog rows store `elements` as
    // a comma-separated string ("Air", "Air, Fire"), live instances
    // may carry a string array, and NORMALIZED catalog cards carry an
    // object array ([{ id: 'fire', name: 'Fire' }]). Normalise all of
    // them to a lowercase token list.
    const raw = card.elements;
    let els = [];
    if (Array.isArray(raw)) els = raw;
    else if (typeof raw === 'string') els = raw.split(/[,;\s]+/).filter(Boolean);
    const elsLower = els.map((e) => {
      if (e && typeof e === 'object') return String(e.id || e.name || '').toLowerCase();
      return String(e).toLowerCase();
    });
    if (!elsLower.includes(wanted)) {
      // Codex Flooded: "A flooded site has a minimum of one water affinity
      // (W). Therefore, it's a water site." Site element filters consult the
      // EFFECTIVE threshold contribution (flooded floor, provides stamps,
      // suppression), not just printed elements. A Geyser-flooded land site
      // is a legal 'water' site target (Riptide).
      if (!(card.isSite || card.type === 'Site')) return false;
      const eff = effectiveSiteThresholds(card, ctx.getFullCard?.(card.cardId) || card);
      if (!(eff[wanted] > 0)) return false;
    }
  }

  // ── Status (aura-aware) ─────────────────────────────────────────
  if (filter.status) {
    const checker = ctx.hasEffectiveStatus || defaultHasEffectiveStatus;
    if (!checker(card, filter.status)) return false;
  }

  // ── Region (surface / subsurface) ───────────────────────────────
  // Reads card.subsurface via the canonical currentRegion helper.
  // 'subsurface' is the virtual any-below-the-surface token (matches
  // both underground and underwater); 'surface'/'underground'/
  // 'underwater' match that exact region. Lets weather/area effects
  // encode "atop" vs "below" (Thunderstorm hits surface units only).
  if (filter.region) {
    const wanted = (Array.isArray(filter.region) ? filter.region : [filter.region])
      .map((r) => String(r).toLowerCase());
    // Full resolver (detects 'void'. A site-less cell) when board cell
    // context is available; falls back to the subsurface-only read so
    // ledger / scan callers without a cell still match surface/under*.
    const cardCell = ctx.meshes && ctx.cellAt ? resolveCardCell(card, ctx) : null;
    const region = cardCell
      ? currentRegionAtCell(card, cardCell, ctx.meshes, ctx.cellAt, ctx.getFullCard)
      : currentRegion(card);
    const ok = wanted.includes(region)
      || (wanted.includes('subsurface') && region !== 'surface' && region !== 'void');
    if (!ok) return false;
  }

  // ── State flags ─────────────────────────────────────────────────
  // `_summonedThisTurn` is the dedicated summon stamp (survives a Charge
  // grant deleting `_hasSummoningSickness`. Savior + Rebecks); the
  // sickness flag stays as a fallback for hosts that only stamp it.
  if (filter.summoned_this_turn === true
      && !card._summonedThisTurn && !card._hasSummoningSickness) return false;
  if (filter.currently_tapped === true && !card.tapped) return false;
  if (filter.currently_tapped === false && card.tapped) return false;

  // ── Cells ──────────────────────────────────────────────────────
  if (Array.isArray(filter.cells) && filter.cells.length > 0) {
    if (ctx.destinationCell) {
      const dc = ctx.destinationCell;
      if (!filter.cells.some((c) => c.col === dc.col && c.row === dc.row)) return false;
    } else {
      const cardCell = resolveCardCell(card, ctx);
      if (cardCell) {
        if (!filter.cells.some((c) => c.col === cardCell.col && c.row === cardCell.row)) return false;
      }
      // No resolver available → field passes. Preserves ledger
      // cost-mod behaviour (`cells` filter is permissive when the
      // caller can't supply a cell context). Forward-compat with
      // future callers that don't pass `cellAt`.
    }
  }

  // ── Site under the card ────────────────────────────────────────
  // `on_site: <CardFilter>`. The SITE at the card's cell must match the
  // subfilter (no site at the cell → no match). Lets area effects gate on
  // the terrain beneath a unit: Atlantean Fate submerges only atop its
  // flooded non-Ordinary sites, so `on_site: {not: {rarity: 'Ordinary'}}`.
  if (filter.on_site) {
    if (!ctx.meshes || !ctx.cellAt) return false;
    const cardCell = resolveCardCell(card, ctx);
    if (!cardCell) return false;
    const site = siteAtCell(ctx.meshes, ctx.cellAt, cardCell);
    if (!site || !matchesFilter(site, filter.on_site, ctx)) return false;
  }

  return true;
}
